import {
  Badge, Button, Container, Group, Pagination, Stack,
  Table, Text, Title, Tooltip,
} from '@mantine/core';
import { IconFilterOff, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { accountsExportHref, type AccountsListParams } from '../api/accounts';
import type { LifecycleStatus } from '../api/types';
import {
  ColumnMultiSelectFilter, ColumnTextFilter,
  hasActiveFilters,
} from '../components/ColumnFilters';
import { AgreementRef, InsurancePolicyRef } from '../components/EntityRef';
import { ExportCsvButton } from '../components/ExportCsvButton';
import { NO_STATUS_SELECTED_HINT, StatusFilterPills } from '../components/StatusFilterPills';
import { useAccounts } from '../hooks/useAccounts';
import { PendingCRBadge } from '../components/PendingCRBadge';
import {
  SortableTh, nextSort, sortParam, type SortState,
} from '../components/SortableHeader';
import { StatusExplained } from '../components/StatusExplained';
// useDeleteAccount / useRestoreAccount / useConfirmDelete removed 2026-06-02
// (Task #245) — destructive actions live exclusively on the detail page.

const PAGE_SIZE = 50;
type AccountSortKey = 'mnemonic' | 'bank' | 'internal' | 'iban' | 'agreement' | 'policy' | 'rto' | 'status';

export function AccountsListPage() {
  const navigate = useNavigate();
  // Banks smoke (Pedro 2026-10-20): Admin → Banks links a bank's account count here as
  // ?mnemonic=XXX — every status, so an Inactive or Closed account is not hidden.
  const [searchParams] = useSearchParams();
  const linkedMnemonic = searchParams.get('mnemonic') ?? '';
  const [page, setPage] = useState(1);
  // #277 polish (2026-06-08): cumulative status filter — default Active only.
  const [statusFilter, setStatusFilter] = useState<LifecycleStatus[]>(
    linkedMnemonic ? ['Active', 'Inactive', 'Closed'] : ['Active']);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  // #39 (2026-06-11): four-bucket pills — zero selected = no fetch + hint.
  const hasSelection = statusFilter.length > 0 || includeDeleted;

  // ── #46 Wave 1 (2026-06-12): per-column predicate filters (C-132) ──────
  // Replaced the old global search box; agreement_q matches the EFFECTIVE
  // agreement (direct or via Policy — same semantics the column shows).
  // Pedro 2026-10-20 (Banks list, 0112): the bank's mnemonic is the first column.
  const [mnemonicQ, setMnemonicQ] = useState(linkedMnemonic);
  const [bankQ, setBankQ] = useState('');
  const [internalQ, setInternalQ] = useState('');
  const [ibanQ, setIbanQ] = useState('');
  const [agreementQ, setAgreementQ] = useState('');
  const [policyQ, setPolicyQ] = useState('');
  const [rtoF, setRtoF] = useState<string[]>([]);

  // P41 (2026-10-15): header sort, server-side across pages; null = by bank.
  const [sort, setSortState] = useState<SortState<AccountSortKey> | null>(null);
  function onSort(k: AccountSortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }

  const filtersActive = hasActiveFilters({
    mnemonicQ, bankQ, internalQ, ibanQ, agreementQ, policyQ, rtoF,
  });
  function clearFilters() {
    setMnemonicQ(''); setBankQ(''); setInternalQ(''); setIbanQ('');
    setAgreementQ(''); setPolicyQ(''); setRtoF([]);
    setPage(1);
  }
  function paged<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  const listParams: AccountsListParams = {
    status: statusFilter.length > 0 ? statusFilter : undefined,
    include_deleted: includeDeleted,
    mnemonic_q: mnemonicQ || undefined,
    bank_q: bankQ || undefined,
    internal_q: internalQ || undefined,
    iban_q: ibanQ || undefined,
    agreement_q: agreementQ || undefined,
    policy_q: policyQ || undefined,
    rto: rtoF.length > 0 ? rtoF.map((v) => v === 'true') : undefined,
    sort: sortParam(sort),
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  };
  const { data, isLoading, error } = useAccounts(listParams, { enabled: hasSelection });
  // del / restore / confirmDelete removed (Task #245, 2026-06-02).
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Container size="xl">
      <Stack>
        <Group justify="space-between">
          <Title order={2}>Accounts</Title>
          <Group gap="sm">
            {/* Pedro 2026-10-10: the list export, like Agreements and Persons (Admin-only). */}
            <ExportCsvButton href={accountsExportHref(listParams)} hasSelection={hasSelection} />
            <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/accounts/new')}>
              New account
            </Button>
          </Group>
        </Group>

        {/* #46 (2026-06-12): the old global search box was replaced by
            the per-column header filters below (C-132). */}
        <StatusFilterPills
          entity="account"
          statuses={statusFilter}
          onStatusesChange={(next) => { setStatusFilter(next); setPage(1); }}
          includeDeleted={includeDeleted}
          onIncludeDeletedChange={(next) => { setIncludeDeleted(next); setPage(1); }}
        />

        {error && <Text c="red">{error.detail}</Text>}

        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              {/* P41 (2026-10-15): every header sorts (server-side, across pages). */}
              <SortableTh label="Mnemonic" col="mnemonic" sort={sort} onSort={onSort} filtered={!!mnemonicQ} w={110} />
              <SortableTh label="Bank" col="bank" sort={sort} onSort={onSort} filtered={!!bankQ} />
              <SortableTh label="Internal #" col="internal" sort={sort} onSort={onSort} filtered={!!internalQ} />
              <SortableTh label="IBAN" col="iban" sort={sort} onSort={onSort} filtered={!!ibanQ} />
              {/* Phase A3.e (2026-06-09): two parent columns instead of one.
                  Direct accounts show the Agreement only; Indirect show
                  the Agreement (via Policy, dimmed) AND the Policy itself;
                  orphans show "—" on both. The Agreement column reads
                  `effective_agreement_id` (server-resolved through the
                  Policy for Indirect), not raw `agreement_id`. */}
              <SortableTh label="Agreement" col="agreement" sort={sort} onSort={onSort} filtered={!!agreementQ} />
              <SortableTh label="Policy" col="policy" sort={sort} onSort={onSort} filtered={!!policyQ} />
              <SortableTh label="RTO" col="rto" sort={sort} onSort={onSort} filtered={rtoF.length > 0} w={120} />
              <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w={200} />
              {/* Actions column removed 2026-06-02 (Task #245). */}
            </Table.Tr>
            {/* #46 Wave 1 (2026-06-12): per-column filter row (C-132).
                Status is owned by the pills (one axis, one control). */}
            <Table.Tr>
              <Table.Th>
                <ColumnTextFilter value={mnemonicQ} onChange={paged(setMnemonicQ)}
                  placeholder="Mnemonic…" ariaLabel="Filter by bank mnemonic" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={bankQ} onChange={paged(setBankQ)}
                  placeholder="Bank…" ariaLabel="Filter by bank" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={internalQ} onChange={paged(setInternalQ)}
                  placeholder="Internal #…" ariaLabel="Filter by internal number" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={ibanQ} onChange={paged(setIbanQ)}
                  placeholder="IBAN…" ariaLabel="Filter by IBAN" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={agreementQ} onChange={paged(setAgreementQ)}
                  placeholder="Code / mnemonic…" ariaLabel="Filter by agreement" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={policyQ} onChange={paged(setPolicyQ)}
                  placeholder="Insurer / policy #…" ariaLabel="Filter by policy" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={rtoF} onChange={paged(setRtoF)}
                  options={[
                    { value: 'true', label: 'RTO' },
                    { value: 'false', label: 'No RTO' },
                  ]}
                  allLabel="All" ariaLabel="Filter by RTO approval" />
              </Table.Th>
              <Table.Th>
                {filtersActive && (
                  <Tooltip label="Clear all column filters" withArrow>
                    <Button size="compact-xs" variant="subtle" color="gray"
                      leftSection={<IconFilterOff size={14} />}
                      onClick={clearFilters}>
                      Clear
                    </Button>
                  </Tooltip>
                )}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {/* #39: zero pills → hint instead of data (no fetch fired). */}
            {!hasSelection && (
              <Table.Tr><Table.Td colSpan={8}><Text c="dimmed">{NO_STATUS_SELECTED_HINT}</Text></Table.Td></Table.Tr>
            )}
            {hasSelection && isLoading && <Table.Tr><Table.Td colSpan={8}><Text c="dimmed">Loading...</Text></Table.Td></Table.Tr>}
            {/* #46 / C-132: two-tier empty state — also fixes a latent
                C-131 gating gap (this row was not hasSelection-gated). */}
            {hasSelection && !isLoading && data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  {filtersActive ? (
                    <Group gap="xs" justify="center" py="sm">
                      <Text c="dimmed" size="sm">No accounts match the current filters.</Text>
                      <Button size="compact-xs" variant="subtle"
                        leftSection={<IconFilterOff size={14} />}
                        onClick={clearFilters}>
                        Clear filters
                      </Button>
                    </Group>
                  ) : (
                    <Text c="dimmed">No accounts found.</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {hasSelection && data?.items.map((a) => {
              // Phase A3.e (2026-06-09): Direct/Indirect/Orphan rendering.
              const isIndirect = !!a.opened_by_policy_id;
              return (
              <Table.Tr
                key={a.id}
                style={{ cursor: 'pointer', opacity: a.deleted_at ? 0.5 : 1 }}
                onClick={() => navigate(`/accounts/${a.id}`)}
              >
                <Table.Td><Text ff="monospace" fw={500}>{a.bank_mnemonic ?? '—'}</Text></Table.Td>
                <Table.Td><Text fw={500}>{a.bank_name}</Text></Table.Td>
                <Table.Td><Text ff="monospace">{a.internal_num}</Text></Table.Td>
                <Table.Td><Text ff="monospace" size="sm">{a.iban ?? '—'}</Text></Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  {/* Phase A3.e: read `effective_agreement_id` (resolved
                      through Policy for Indirect) instead of raw
                      `agreement_id`. Em-dash when neither FK is set
                      (orphan). The dimmed "via Policy" suffix flags
                      Indirect rows so the visual is distinguishable
                      from Direct at a glance. */}
                  {a.effective_agreement_id ? (
                    <Group gap={4} wrap="nowrap" align="baseline">
                      <AgreementRef id={a.effective_agreement_id} linkPathPrefix="/agreements" />
                      {isIndirect && <Text size="xs" c="dimmed">via Policy</Text>}
                    </Group>
                  ) : (
                    <Text size="xs" c="dimmed">—</Text>
                  )}
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <InsurancePolicyRef id={a.opened_by_policy_id} linkPathPrefix="/account-insurance" />
                </Table.Td>
                <Table.Td>
                  {a.rto_approved
                    ? <Badge color="blue" variant="light">RTO</Badge>
                    : <Text c="dimmed">—</Text>}
                </Table.Td>
                <Table.Td>
                  <Stack gap={4} align="flex-start">
                    {/* P47: the badge explains its status on hover (same words as the pills). */}
                    <StatusExplained entity="account" status={a.deleted_at ? 'Deleted' : a.status}>
                    {a.deleted_at
                      ? <Badge color="gray" variant="light">Deleted</Badge>
                      : <Badge
                          color={
                            a.status === 'Active' ? 'green'
                              : a.status === 'Inactive' ? 'yellow'
                              : 'gray'  // Closed
                        }
                          variant="light"
                        >{a.status}</Badge>}
                    </StatusExplained>
                    <PendingCRBadge crs={a.pending_change_requests} />
                  </Stack>
                </Table.Td>
                {/* Actions column removed (Task #245 2026-06-02). */}
              </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        {data && data.total > PAGE_SIZE && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{data.total} total</Text>
            <Pagination total={totalPages} value={page} onChange={setPage} />
          </Group>
        )}
      </Stack>
    </Container>
  );
}
